// Shared ReportDataGrid wiring for the report-schedule tables.
//
// ReportDataGrid owns the pagination/sort/filter model (it persists them per
// user) and reports the resulting query back through `onQueryStateChange`.
// That is the supported v6 path: the removed v5 props (`page`, `pageSize`,
// `onPageChange`, `rowsPerPageOptions`) are silently ignored by the installed
// grid, which is why the old footer claimed the server's full total while
// only the first page of rows existed and the next arrow was dead.

export const UNIFIED_SCHEDULE_FIELDS = [
    'title',
    'report_type',
    'kind',
    'locations',
    'period',
    'formats',
    'timezone',
    'recipients_count',
    'next_run_at',
    'latest_result',
    'is_enabled',
    'actions',
];

export const UNIFIED_SCHEDULE_GRID_DEFAULTS = {
    columns: UNIFIED_SCHEDULE_FIELDS,
    columnOrder: UNIFIED_SCHEDULE_FIELDS,
    // Thirteen columns do not fit a laptop at rest, and a table of truncated
    // headers is worse than a shorter one. These three are hidden by default
    // and stay available from the Columns menu: the report type and the kind
    // are already the caption under each report's name, and the timezone is
    // in the Next delivery tooltip.
    columnVisibilityModel: { report_type: false, kind: false, timezone: false },
    columnWidths: {},
    // The row's actions were being clipped off-screen at 1440 until the user
    // discovered the horizontal scroll. Pinned right, they are always there.
    pinnedColumns: { left: [], right: ['actions'] },
    sortModel: [],
    filterModel: { items: [] },
    paginationModel: { page: 0, pageSize: 25 },
};

const RUN_HISTORY_FIELDS = ['title', 'is_test', 'period', 'recipients', 'status', 'created_at', 'actions'];

export const RUN_HISTORY_GRID_DEFAULTS = {
    columns: RUN_HISTORY_FIELDS,
    columnOrder: RUN_HISTORY_FIELDS,
    columnVisibilityModel: {},
    columnWidths: {},
    pinnedColumns: { left: [], right: [] },
    sortModel: [],
    filterModel: { items: [] },
    paginationModel: { page: 0, pageSize: 25 },
};

// Translates the grid's query state into what the fetching hooks ask the
// server for. Returns the previous object unchanged when nothing moved, so a
// re-render cannot trigger a refetch.
export const scheduleQueryHandler = (setQuery) => ({ paginationModel }) => {
    setQuery((previous) => {
        const { page, pageSize } = paginationModel;
        if (previous.page === page && previous.pageSize === pageSize) return previous;
        return { ...previous, page, pageSize };
    });
};

// Same, for the unified table, which also sorts on the server.
export const unifiedQueryHandler = (setQuery, toOrdering) => ({ paginationModel, sortModel }) => {
    setQuery((previous) => {
        const { page, pageSize } = paginationModel;
        const ordering = toOrdering(sortModel);
        if (previous.page === page && previous.pageSize === pageSize && previous.ordering === ordering) {
            return previous;
        }
        // A new sort describes a different sequence, so the page index from
        // the old one means nothing; start again at the first page.
        const sortChanged = previous.ordering !== ordering;
        return { ...previous, page: sortChanged ? 0 : page, pageSize, ordering };
    });
};
